
/**
 * Any courseAdministration API calls with React Query.
 */
import { useMemo } from "react";
import moment from "moment";
import { GetCouresListDropdownCancelToken, GetCourselistCancelToken, GetQACourselistCancelToken } from "../../services/DashBoardServices";
import { enhanceCourseDropdownWithIntakes } from "../../utils/intakeOptions";
import { QUERY_CACHE_PROFILES, QUERY_KEYS } from "../queryConsts";
import { useBaseReactQuery } from "./useBaseReactQuery";

const EMPTY_COURSE_DROPDOWN_RAW = Object.freeze({
    programme: [],
    intake: [],
    learning: [],
    school: [],
    type: [],
    subjectItemsArray: [],
    subjectItems: []
});

const EMPTY_COURSE_LIST = Object.freeze({
    courseList: [],
    totalRows: 0
});

export const CourseAdministrationTabNames = {
    AllCourses: "All Courses",
    InProgress: "In Progress",
    SelfPaced: "Self Paced",
    Upcoming: "Upcoming",
    Past: "Past",
    AuditTrails: "Audit Trails"
};

/**
 * Courses Dropdown - PERSISTED
 * @param {CourseAdministrationTabNames} Tab Courses Tab name.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useCourseDropdown(filters) {
    const query = useBaseReactQuery(
        [QUERY_KEYS.courseAdministration.root, QUERY_KEYS.courseAdministration.courseDropdown, filters],
        async () => {
            const { data } = await GetCouresListDropdownCancelToken(filters);

            return data;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.LOOKUP
        }
    );

    return {
        ...query,
        data: query.data ?? EMPTY_COURSE_DROPDOWN_RAW
    };
}

/**
 * React Query hook for course lists.
 * The requestData should already be in the format expected by the API.
 *
 * @param {object} requestData Request body passed directly to the API.
 * @param {boolean} QAview If true, uses the QA course list endpoint.
 * @returns {DefinedUseQueryResult}
 */
export function useCourseList(requestData, QAview = false) {
    const query = useBaseReactQuery(
        [
            QUERY_KEYS.courseAdministration.root,
            QUERY_KEYS.courseAdministration.courseList,
            QAview,
            requestData
        ],
        async () => {
            const { data } = QAview
                ? await GetQACourselistCancelToken(requestData)
                : await GetCourselistCancelToken(requestData);

            return {
                courseList: data?.result ?? [],
                totalRows: data?.Pagination?.total ?? 0
            };
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.TABLE
        }
    );

    return {
        ...query,
        data: query.data ?? EMPTY_COURSE_LIST
    };
}
